import React, {useCallback, useContext, useState} from 'react';
import {Box, Typography, useTheme} from '@mui/material';
import {useDropzone, FileWithPath} from 'react-dropzone';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {HausverwaltungService} from '../../services/openapi';
import APIWrapperContext from '../../services/APIWrapperContext';

interface FileDropZoneProps {
  dir_id: string;
  onUploadStart?: (files: FileWithPath[]) => void;
  onUploadSuccess?: (files: FileWithPath[]) => void;
  onUploadError?: (files: FileWithPath[]) => void;
}

export const FileDropZone: React.FC<FileDropZoneProps> = ({
  dir_id,
  onUploadStart,
  onUploadSuccess,
  onUploadError,
}) => {
  const theme = useTheme();
  const {call_backend_api} = useContext(APIWrapperContext);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const onDrop = useCallback(
    (acceptedFiles: FileWithPath[]) => {
      setIsUploading(true);
      
      // Notify parent about upload start
      if (onUploadStart) {
        onUploadStart(acceptedFiles);
      }
      
      let filesProcessed = 0;
      const totalFiles = acceptedFiles.length;
      
      acceptedFiles.forEach((file: FileWithPath) => {
        call_backend_api(
          HausverwaltungService.uploadFileHausverwaltungAddFileToHausverwaltungPost({
            dir_id: dir_id,
            file: file,
          }),
          (response: any) => {
            console.log('File uploaded successfully:', response);
            if (onUploadSuccess) {
              onUploadSuccess([file]);
            }
            filesProcessed++;
            if (filesProcessed === totalFiles) {
              setIsUploading(false);
            }
          },
          (error: {status: number; data?: {msg?: string}}) => {
            if (error.status === 409) {
              console.log('File already exists');
            }
            if (onUploadError) {
              onUploadError([file]);
            }
            filesProcessed++;
            if (filesProcessed === totalFiles) {
              setIsUploading(false);
            }
          },
          () => {
            // This callback is called regardless of success/error
          },
          true,
          'Datei erfolgreich hochgeladen',
          'Fehler beim Hochladen der Datei',
          true
        );
      });
    },
    [dir_id, call_backend_api, onUploadStart, onUploadSuccess, onUploadError]
  );

  const {getRootProps, getInputProps, isDragReject} = useDropzone({
    onDrop,
    onDragEnter: () => setIsDragActive(true),
    onDragLeave: () => setIsDragActive(false),
    onDropAccepted: () => setIsDragActive(false),
    onDropRejected: () => setIsDragActive(false),
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg'],
      'application/pdf': ['.pdf']
    },
    multiple: true
  });

  return (
    <Box
      {...getRootProps()}
      sx={{
        border: `2px dashed ${
          isDragReject ? theme.palette.error.main : 
          isDragActive ? theme.palette.primary.main : 
          theme.palette.grey[400]
        }`,
        borderRadius: 1,
        p: 3,
        m: 2,
        backgroundColor: isDragActive ? theme.palette.action.hover : theme.palette.background.paper,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        '&:hover': {
          borderColor: theme.palette.primary.main,
          backgroundColor: theme.palette.action.hover,
        },
        opacity: isUploading ? 0.6 : 1,
      }}
    >
      <input {...getInputProps()} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
        }}
      >
        <CloudUploadIcon 
          sx={{ 
            fontSize: 48, 
            color: isDragReject ? theme.palette.error.main : 
                   isDragActive ? theme.palette.primary.main : 
                   theme.palette.grey[500] 
          }} 
        />
        <Typography variant="body1" color="textSecondary" align="center">
          {isUploading 
            ? 'Hochladen...' 
            : isDragReject
            ? 'Nur Bilder und PDFs erlaubt!'
            : isDragActive 
            ? 'Dateien hier ablegen' 
            : 'Dateien hier ablegen oder klicken zum Auswählen'
          }
        </Typography>
      </Box>
    </Box>
  );
};
